import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Search, Eye, Calendar, Car, User,
    CheckCircle, XCircle, AlertCircle
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { adminApi } from '../../lib/api'

interface Booking {
    id: number
    customer_name: string
    customer_id: number
    vehicle_title_en: string
    vehicle_title_vi: string
    vehicle_id: number
    vehicle_type: string
    owner_name: string
    start_date: string
    end_date: string
    total_price: number
    status: string
    payment_status: string
    created_at: string
}

interface Stats {
    total: number
    pending: number
    confirmed: number
    in_progress: number
    completed: number
    cancelled: number
    total_revenue: number
}

export default function BookingManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [showUpdateModal, setShowUpdateModal] = useState(false)
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
    const [bookings, setBookings] = useState<Booking[]>([])
    const [stats, setStats] = useState<Stats>({ total: 0, pending: 0, confirmed: 0, in_progress: 0, completed: 0, cancelled: 0, total_revenue: 0 })

    const statuses = [
        { id: 'all', name: { en: 'All', vi: 'Tất cả' } },
        { id: 'pending', name: { en: 'Pending', vi: 'Chờ xử lý' } },
        { id: 'confirmed', name: { en: 'Confirmed', vi: 'Đã xác nhận' } },
        { id: 'in_progress', name: { en: 'In Progress', vi: 'Đang thuê' } },
        { id: 'completed', name: { en: 'Completed', vi: 'Hoàn thành' } },
        { id: 'cancelled', name: { en: 'Cancelled', vi: 'Đã hủy' } },
    ]

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                const params: any = { search: searchQuery }
                if (statusFilter !== 'all') params.status = statusFilter

                const response = await adminApi.getBookings(params)
                if (response.data?.data) {
                    setBookings(response.data.data)
                }
                if (response.data?.stats) {
                    setStats(response.data.stats)
                }
            } catch (error) {
                console.error('Failed to fetch bookings:', error)
            }
        }
        fetchBookings()
    }, [statusFilter, searchQuery])

    const filteredBookings = bookings
    const [newStatus, setNewStatus] = useState('in_progress')

    const handleUpdateStatus = async () => {
        if (!selectedBooking) return
        try {
            // API call would go here
            // await adminApi.updateBookingStatus(selectedBooking.id, newStatus)
            setBookings(prev => prev.map(b =>
                b.id === selectedBooking.id ? { ...b, status: newStatus } : b
            ))
            setShowUpdateModal(false)
            alert(language === 'en' ? 'Status updated!' : 'Đã cập nhật!')
        } catch (error) {
            console.error('Failed to update status:', error)
            alert(language === 'en' ? 'Failed to update' : 'Lỗi khi cập nhật')
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-700'
            case 'confirmed': return 'bg-blue-100 text-blue-700'
            case 'in_progress': return 'bg-purple-100 text-purple-700'
            case 'completed': return 'bg-green-100 text-green-700'
            case 'cancelled': return 'bg-red-100 text-red-700'
            default: return 'bg-gray-100 text-gray-700'
        }
    }

    const getStatusLabel = (status: string) => {
        const labels: Record<string, { en: string; vi: string }> = {
            pending: { en: 'Pending', vi: 'Chờ xử lý' },
            confirmed: { en: 'Confirmed', vi: 'Đã xác nhận' },
            in_progress: { en: 'In Progress', vi: 'Đang thuê' },
            completed: { en: 'Completed', vi: 'Hoàn thành' },
            cancelled: { en: 'Cancelled', vi: 'Đã hủy' },
        }
        return labels[status]?.[language] || status
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">
                    {language === 'en' ? 'Booking Management' : 'Quản Lý Đơn Đặt'}
                </h1>
                <p className="text-gray-500 mt-1">
                    {language === 'en' ? 'View and manage all bookings' : 'Xem và quản lý tất cả đơn đặt'}
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total Bookings' : 'Tổng đơn'}</p>
                    <p className="text-2xl font-bold mt-1">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Pending' : 'Chờ xử lý'}</p>
                    <p className="text-2xl font-bold mt-1 text-yellow-600">{stats.pending}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Active' : 'Đang hoạt động'}</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600">{(stats.confirmed || 0) + (stats.in_progress || 0)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Revenue' : 'Doanh thu'}</p>
                    <p className="text-xl font-bold mt-1 text-green-600">{formatCurrency(stats.total_revenue || 0, 'VND')}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            placeholder={language === 'en' ? 'Search by ID, customer, or vehicle...' : 'Tìm theo mã, khách hàng, hoặc xe...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto">
                        {statuses.map((status) => (
                            <button
                                key={status.id}
                                onClick={() => setStatusFilter(status.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${statusFilter === status.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {status.name[language]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bookings Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Booking' : 'Đơn đặt'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                                    {language === 'en' ? 'Customer' : 'Khách hàng'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                    {language === 'en' ? 'Dates' : 'Ngày thuê'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                    {language === 'en' ? 'Total' : 'Tổng tiền'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Status' : 'Trạng thái'}
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                    {language === 'en' ? 'Actions' : 'Thao tác'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredBookings.map((booking) => (
                                <tr key={booking.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                                <Car className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="font-medium">#{booking.id}</p>
                                                <p className="text-sm text-gray-500">{language === 'en' ? booking.vehicle_title_en : (booking.vehicle_title_vi || booking.vehicle_title_en)}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden md:table-cell">
                                        <Link to={`/admin/users/${booking.customer_id}`} className="flex items-center gap-2 text-primary-600 hover:underline">
                                            <User className="w-4 h-4" />
                                            {booking.customer_name || 'Guest'}
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <div className="text-sm">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                {booking.start_date} → {booking.end_date}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <div>
                                            <p className="font-medium text-green-600">{formatCurrency(booking.total_price, 'VND')}</p>
                                            <p className="text-xs text-gray-500">{booking.payment_status}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(booking.status)}`}>
                                            {getStatusLabel(booking.status)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button className="p-2 hover:bg-gray-100 rounded-lg" title="View Details">
                                                <Eye className="w-4 h-4 text-gray-600" />
                                            </button>
                                            {booking.status === 'pending' && (
                                                <>
                                                    <button className="p-2 hover:bg-green-50 rounded-lg" title="Confirm">
                                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                                    </button>
                                                    <button className="p-2 hover:bg-red-50 rounded-lg" title="Cancel">
                                                        <XCircle className="w-4 h-4 text-red-600" />
                                                    </button>
                                                </>
                                            )}
                                            {booking.status === 'confirmed' && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedBooking(booking)
                                                        setShowUpdateModal(true)
                                                    }}
                                                    className="p-2 hover:bg-purple-50 rounded-lg"
                                                    title="Start"
                                                >
                                                    <AlertCircle className="w-4 h-4 text-purple-600" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Update Status Modal */}
            {showUpdateModal && selectedBooking && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-4">
                            {language === 'en' ? 'Update Booking Status' : 'Cập Nhật Trạng Thái'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Booking #{selectedBooking.id} - {language === 'en' ? selectedBooking.vehicle_title_en : (selectedBooking.vehicle_title_vi || selectedBooking.vehicle_title_en)}
                        </p>
                        <select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg mb-4"
                        >
                            <option value="confirmed">{language === 'en' ? 'Confirmed' : 'Đã xác nhận'}</option>
                            <option value="in_progress">{language === 'en' ? 'In Progress' : 'Đang thuê'}</option>
                            <option value="completed">{language === 'en' ? 'Completed' : 'Hoàn thành'}</option>
                            <option value="cancelled">{language === 'en' ? 'Cancelled' : 'Đã hủy'}</option>
                        </select>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                            <button
                                onClick={handleUpdateStatus}
                                className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                {language === 'en' ? 'Update' : 'Cập nhật'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
